type MarketplacePurchaseLibraryIdentity = {
  marketplaceItemId?: null | string;
  workoutId?: string;
  programId?: string;
  exerciseId?: string;
  mealId?: string;
  nutritionPlanId?: string;
  episodeId?: string;
  moduleId?: string;
  courseId?: string;
};

type MarketplacePurchaseReturnIdentity = {
  listingId?: string;
  legacyContentId?: string;
};

/**
 * Whether a URL's query carries a checkout return that
 * `MarketplacePurchaseReturn` acts on: a cancel, or a success with the
 * listing (or, for a provider session opened before the listing marker, the
 * legacy content) it polls for. A success marker with nothing to wait for is
 * not one — the handler would render and clear nothing.
 */
export const isMarketplacePurchaseReturn = (
  params: Record<string, string | undefined>,
) =>
  params.purchase === 'cancel' ||
  (params.purchase === 'success' &&
    Boolean(params.purchasedListing || params.purchasedItem));

export function isMarketplacePurchaseDelivered(
  rows: MarketplacePurchaseLibraryIdentity[],
  { legacyContentId, listingId }: MarketplacePurchaseReturnIdentity,
) {
  return rows.some(
    (row) =>
      (!!listingId && row.marketplaceItemId === listingId) ||
      (!!legacyContentId &&
        (row.workoutId === legacyContentId ||
          row.programId === legacyContentId ||
          row.exerciseId === legacyContentId ||
          row.mealId === legacyContentId ||
          row.nutritionPlanId === legacyContentId ||
          row.episodeId === legacyContentId ||
          row.moduleId === legacyContentId ||
          row.courseId === legacyContentId)),
  );
}

type MarketplacePurchaseStatusRow = {
  marketplaceItemId?: null | string;
  contentType?: null | string;
  fulfillmentStatus?: null | string;
  orderRef?: null | string;
};

export type MarketplacePurchaseOutcome =
  | 'contentDelivered'
  | 'contentRefundPending'
  | 'contentRefunded'
  | 'pending'
  | 'seatBooked'
  | 'seatRefundPending'
  | 'seatRefunded'
  | 'unavailable';

const REFUND_PENDING_STATES = new Set([
  'refund_pending',
  'refund_processing',
  'refund_retryable',
]);
const UNAVAILABLE_STATES = new Set(['refund_failed', 'unavailable']);

/**
 * What the return page should say, from the two things the buyer can read
 * about their own purchase: the library (content lands there) and the purchase
 * history (a SEAT never produces a library row — its outcome is the
 * fulfillment state on the charge, which also carries the capacity-loss refund).
 *
 * `pending` keeps the poll running; every other value ends it.
 *
 * The history lists only PAID charges, so the charge this return belongs to
 * is absent while it is still pending — and the newest paid row for the
 * listing may be an OLDER purchase, terminal by definition (refunded after a
 * capacity loss, or after the buyer cancelled and is buying the same event
 * again). The checkout stamps its `orderRef` onto the return URL; when it is
 * present only that charge is consulted. Without it (a provider session
 * opened before this deployment) the newest row of the listing is used.
 */
export function resolveMarketplacePurchaseOutcome({
  legacyContentId,
  libraryRows,
  listingId,
  orderRef,
  purchases,
}: {
  libraryRows: MarketplacePurchaseLibraryIdentity[];
  purchases: MarketplacePurchaseStatusRow[];
  legacyContentId?: string;
  listingId?: string;
  orderRef?: string;
}): MarketplacePurchaseOutcome {
  if (
    isMarketplacePurchaseDelivered(libraryRows, { legacyContentId, listingId })
  ) {
    return 'contentDelivered';
  }
  if (!listingId) return 'pending';
  const purchase = orderRef
    ? purchases.find((row) => row.orderRef === orderRef)
    : purchases.find((row) => row.marketplaceItemId === listingId);
  const status = purchase?.fulfillmentStatus ?? null;
  if (!status) return 'pending';
  // A refund is seat-shaped only for a seat: `applyMarketplaceHooks` writes
  // `refunded` for ANY refunded marketplace charge, so a dashboard refund of
  // a workout lands here too and must not be told "the session filled up" (R4)
  const isSeat = purchase?.contentType === 'bookingEvent';
  if (REFUND_PENDING_STATES.has(status)) {
    return isSeat ? 'seatRefundPending' : 'contentRefundPending';
  }
  if (status === 'refunded') return isSeat ? 'seatRefunded' : 'contentRefunded';
  if (UNAVAILABLE_STATES.has(status)) return 'unavailable';
  if (status === 'delivered') {
    // a delivered CONTENT purchase shows up in the library first; if the
    // library read lags the fulfillment write, the next poll catches it
    return isSeat ? 'seatBooked' : 'pending';
  }
  return 'pending';
}
